import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/api";
import IntelCard from "./IntelCard";
import ImageViewerModal from "./ImageViewerModal";
import IntelDetailModal from "./modals/IntelDetailModal";

/**
 * IntelLibrary - Browse all intel with filters
 */
const IntelLibrary = ({ accountId, onIntelClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [intel, setIntel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [textFilter, setTextFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [viewingImageIndex, setViewingImageIndex] = useState(null);
  const [viewingImageUrl, setViewingImageUrl] = useState(null);
  const [detailIntelId, setDetailIntelId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Get filters from URL params
  const tickerFilter = searchParams.get("ticker") || "";
  const sortBy = searchParams.get("sort_by") || "captured_at";

  // Determine if we have client-side filters active
  const hasClientFilters = textFilter || startDateFilter || endDateFilter;

  // Fetch intel
  const fetchIntel = async () => {
    try {
      setLoading(true);
      const params = { sort_by: sortBy };

      if (tickerFilter) {
        params.ticker = tickerFilter.toUpperCase();
      }

      // If we have client-side filters, fetch all items (up to 1000)
      // Otherwise use backend pagination
      if (hasClientFilters) {
        params.page = 1;
        params.per_page = 1000;
      } else {
        params.page = currentPage;
        params.per_page = itemsPerPage;
      }

      const response = await api.get(`/intel`, { params });

      // Handle paginated response
      if (response.data.items) {
        setIntel(response.data.items);
        setTotalItems(response.data.total);
      } else {
        // Fallback for old API format
        setIntel(response.data);
        setTotalItems(response.data.length);
      }
    } catch (error) {
      console.error("Error fetching intel:", error);
      setIntel([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntel();
  }, [tickerFilter, sortBy, currentPage, hasClientFilters]);

  // Client-side filtering for text and date (since backend doesn't support these filters yet)
  const filteredIntel = intel.filter((item) => {
    // Text filter (search in thesis, author, platform)
    if (textFilter) {
      const searchText = textFilter.toLowerCase();
      const matchesThesis = item.thesis?.toLowerCase().includes(searchText);
      const matchesAuthor = item.author?.toLowerCase().includes(searchText);
      const matchesPlatform = item.platform?.toLowerCase().includes(searchText);
      const matchesTags = item.tags?.some((tag) =>
        tag.toLowerCase().includes(searchText),
      );

      if (
        !matchesThesis &&
        !matchesAuthor &&
        !matchesPlatform &&
        !matchesTags
      ) {
        return false;
      }
    }

    // Date filter
    if (startDateFilter) {
      const itemDate = new Date(item.timestamp);
      const startDate = new Date(startDateFilter);
      if (itemDate < startDate) return false;
    }

    if (endDateFilter) {
      const itemDate = new Date(item.timestamp);
      const endDate = new Date(endDateFilter);
      endDate.setHours(23, 59, 59, 999); // Include entire end day
      if (itemDate > endDate) return false;
    }

    return true;
  });

  // Backend handles sorting via sort_by parameter, no client-side sorting needed
  const sortedIntel = filteredIntel;

  // Pagination
  // When using backend pagination (no client filters), use the full dataset
  // When using client filters, paginate the filtered results
  let paginatedIntel;
  let totalPages;
  let startIndex;
  let endIndex;
  let displayTotal;

  if (hasClientFilters) {
    // Client-side pagination when filters are active
    displayTotal = sortedIntel.length;
    totalPages = Math.ceil(displayTotal / itemsPerPage);
    startIndex = (currentPage - 1) * itemsPerPage;
    endIndex = startIndex + itemsPerPage;
    paginatedIntel = sortedIntel.slice(startIndex, endIndex);
  } else {
    // Backend pagination when no client filters
    displayTotal = totalItems;
    totalPages = Math.ceil(displayTotal / itemsPerPage);
    startIndex = (currentPage - 1) * itemsPerPage;
    endIndex = Math.min(startIndex + itemsPerPage, displayTotal);
    paginatedIntel = sortedIntel; // Already paginated by backend
  }

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [tickerFilter, textFilter, startDateFilter, endDateFilter, sortBy]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Get images with screenshots for carousel
  const imagesWithScreenshots = sortedIntel.filter(
    (item) => item.screenshot_data,
  );
  const screenshotUrls = imagesWithScreenshots.map((item) => item.screenshot_data);

  const handleClearFilters = () => {
    setSearchParams({}); // Clear URL params
    setTextFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const updateTickerFilter = (ticker) => {
    const newParams = new URLSearchParams(searchParams);
    if (ticker) {
      newParams.set("ticker", ticker);
    } else {
      newParams.delete("ticker");
    }
    setSearchParams(newParams);
  };

  const handleAuthorClick = (author) => {
    // Set text filter to search for this author
    setTextFilter(author);
  };

  const handleTickerNavigation = (ticker) => {
    navigate(`/ticker/${ticker}${accountId ? `?accountId=${accountId}` : ''}`);
  };

  const updateSortBy = (sort) => {
    const newParams = new URLSearchParams(searchParams);
    if (sort !== "captured_at") {
      newParams.set("sort_by", sort);
    } else {
      newParams.delete("sort_by"); // Default value, don't clutter URL
    }
    setSearchParams(newParams);
  };

  const handleDeleteIntel = async (intelId, e) => {
    // Stop event propagation to prevent clicking the card
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to delete this intel item?")) {
      return;
    }

    try {
      setDeletingId(intelId);
      await api.delete(`/intel/${intelId}`);

      // Refetch data to get updated pagination
      await fetchIntel();
    } catch (error) {
      console.error("Error deleting intel:", error);
      alert("Failed to delete intel");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (
      !window.confirm(
        "This will remove duplicate intel based on filename, keeping only the oldest copy of each. Continue?",
      )
    ) {
      return;
    }

    try {
      setCleaningUp(true);
      const response = await api.post(`/intel/cleanup-duplicates`);

      alert(response.data.message);

      // Refresh intel list
      await fetchIntel();
    } catch (error) {
      console.error("Error cleaning up duplicates:", error);
      alert("Failed to cleanup duplicates");
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Filters - Fixed at top */}
      <div
        className="section-content"
        style={{
          flexShrink: 0,
          background: "var(--section-bg, var(--card-bg, #ffffff))",
          borderBottom: "1px solid var(--form-input-border)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            {/* Ticker Filter */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Ticker:
              </label>
              <input
                type="text"
                value={tickerFilter}
                onChange={(e) =>
                  updateTickerFilter(e.target.value.toUpperCase())
                }
                placeholder="e.g. AAPL"
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                  width: "100px",
                }}
              />
            </div>

            {/* Text Search Filter */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Search:
              </label>
              <input
                type="text"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="thesis, author, tags..."
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                  width: "200px",
                }}
              />
            </div>

            {/* Date Range Filter */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                From:
              </label>
              <input
                type="date"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                To:
              </label>
              <input
                type="date"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
              {(tickerFilter ||
                textFilter ||
                startDateFilter ||
                endDateFilter) && (
                <button
                  onClick={handleClearFilters}
                  className="btn btn-secondary"
                  style={{
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.875rem",
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
            {/* Sort By Dropdown */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <label
                className="text-muted"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Sort:
              </label>
              <select
                value={sortBy}
                onChange={(e) => updateSortBy(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid var(--form-input-border)",
                  borderRadius: "4px",
                  background: "var(--account-type-bg)",
                  color: "var(--title-color)",
                  fontSize: "0.875rem",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="captured_at">Content Date</option>
                <option value="created_at">Create Date</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Loading State */}
        {loading ? (
          <div
            className="text-muted"
            style={{
              padding: "2rem",
              textAlign: "center",
            }}
          >
            Loading intel...
          </div>
        ) : sortedIntel.length === 0 ? (
          /* Empty State */
          <div
            className="text-muted"
            style={{
              padding: "2rem",
              textAlign: "center",
            }}
          >
            {intel.length === 0
              ? "No intel found. Drop a screenshot here to get started!"
              : "No intel matches your filters"}
          </div>
        ) : (
          <>
            {/* Results Summary */}
            <div
              style={{
                padding: "1rem 1.5rem",
                fontSize: "0.875rem",
                color: "#718096",
              }}
            >
              Showing {startIndex + 1}-{Math.min(endIndex, displayTotal)} of{" "}
              {displayTotal} items
            </div>

            {/* Grid View */}
            <div style={{ padding: "0 1.5rem 1.5rem", maxWidth: "900px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                {paginatedIntel.map((item, index) => (
                  <IntelCard
                    key={item.id || index}
                    intel={item}
                    onScreenshotClick={(intel) => {
                      const imageIndex = imagesWithScreenshots.findIndex(
                        (img) => img.id === intel.id,
                      );
                      setViewingImageIndex(imageIndex);
                    }}
                    onImageUrlClick={(url) => setViewingImageUrl(url)}
                    onTickerClick={handleTickerNavigation}
                    onAuthorClick={handleAuthorClick}
                    onDeleteClick={handleDeleteIntel}
                    onDetailClick={(intel) => setDetailIntelId(intel.id)}
                    isDeleting={deletingId === item.id}
                    accountId={accountId}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "2rem 1.5rem",
                  }}
                >
                  {/* Previous Button */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    style={{
                      padding: "0.5rem 1rem",
                      background: currentPage === 1 ? "transparent" : "#3182ce",
                      color:
                        currentPage === 1
                          ? "var(--text-muted, #a0aec0)"
                          : "white",
                      border: "1px solid",
                      borderColor:
                        currentPage === 1
                          ? "var(--border-color, #e2e8f0)"
                          : "#3182ce",
                      borderRadius: "6px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.currentTarget.style.background = "#2c5aa0";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.currentTarget.style.background = "#3182ce";
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {/* Page Numbers */}
                  <div
                    style={{
                      display: "flex",
                      gap: "0.25rem",
                    }}
                  >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (pageNum) => {
                        // Show first page, last page, current page, and pages around current
                        const showPage =
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          Math.abs(pageNum - currentPage) <= 1;

                        // Show ellipsis
                        const showEllipsisBefore =
                          pageNum === currentPage - 2 && currentPage > 3;
                        const showEllipsisAfter =
                          pageNum === currentPage + 2 &&
                          currentPage < totalPages - 2;

                        if (
                          !showPage &&
                          !showEllipsisBefore &&
                          !showEllipsisAfter
                        ) {
                          return null;
                        }

                        if (showEllipsisBefore || showEllipsisAfter) {
                          return (
                            <span
                              key={`ellipsis-${pageNum}`}
                              style={{
                                padding: "0.5rem 0.75rem",
                                color: "var(--text-muted, #a0aec0)",
                              }}
                            >
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                              padding: "0.5rem 0.75rem",
                              background:
                                pageNum === currentPage
                                  ? "#3182ce"
                                  : "transparent",
                              color:
                                pageNum === currentPage
                                  ? "white"
                                  : "var(--title-color, #2d3748)",
                              border: "1px solid",
                              borderColor:
                                pageNum === currentPage
                                  ? "#3182ce"
                                  : "var(--border-color, #e2e8f0)",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontWeight:
                                pageNum === currentPage ? "600" : "500",
                              fontSize: "0.875rem",
                              minWidth: "40px",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              if (pageNum !== currentPage) {
                                e.currentTarget.style.background =
                                  "var(--hover-bg, rgba(49, 130, 206, 0.1))";
                                e.currentTarget.style.borderColor = "#3182ce";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (pageNum !== currentPage) {
                                e.currentTarget.style.background =
                                  "transparent";
                                e.currentTarget.style.borderColor =
                                  "var(--border-color, #e2e8f0)";
                              }
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      },
                    )}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "0.5rem 1rem",
                      background:
                        currentPage === totalPages ? "transparent" : "#3182ce",
                      color:
                        currentPage === totalPages
                          ? "var(--text-muted, #a0aec0)"
                          : "white",
                      border: "1px solid",
                      borderColor:
                        currentPage === totalPages
                          ? "var(--border-color, #e2e8f0)"
                          : "#3182ce",
                      borderRadius: "6px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.currentTarget.style.background = "#2c5aa0";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.currentTarget.style.background = "#3182ce";
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Image Viewer Modal - for screenshot carousel */}
      <ImageViewerModal
        imageUrl={
          viewingImageIndex !== null ? screenshotUrls[viewingImageIndex] : null
        }
        onClose={() => setViewingImageIndex(null)}
        images={screenshotUrls}
        currentIndex={viewingImageIndex}
        onNavigate={setViewingImageIndex}
      />

      {/* Image Viewer Modal - for single image URLs (StockTwits charts) */}
      <ImageViewerModal
        imageUrl={viewingImageUrl}
        onClose={() => setViewingImageUrl(null)}
      />

      {/* Intel pipeline detail modal */}
      {detailIntelId && (
        <IntelDetailModal
          intelId={detailIntelId}
          onClose={() => setDetailIntelId(null)}
        />
      )}
    </div>
  );
};

export default IntelLibrary;
